import type { Product, StockAdjustment } from '@coreledger/shared-types';
import { api } from './client';
import type { PaginationMeta } from './invoices';

export interface Paginated<T> { data: T[]; meta: PaginationMeta }

export const productsApi = {
  list: (search?: string, page = 1, limit = 50) => {
    const p = new URLSearchParams();
    if (search) p.set('search', search);
    p.set('page', String(page));
    p.set('limit', String(limit));
    return api.get<Paginated<Product>>(`/products?${p.toString()}`);
  },
  get: (id: string) => api.get<Product>(`/products/${id}`),
  create: (data: Partial<Product>) => api.post<Product>('/products', data),
  update: (id: string, data: Partial<Product>) => api.patch<Product>(`/products/${id}`, data),
  delete: (id: string) => api.delete<void>(`/products/${id}`),
  bulkDelete: (ids: string[]) => api.patch<{ deleted: number }>('/products/bulk-delete', { ids }),
  deleteAll: () => api.patch<{ deleted: number }>('/products/delete-all', {}),
  openingStockGapCount: () => api.get<{ count: number }>('/products/opening-stock-gap-count'),
  backfillOpeningStock: () => api.patch<{ backfilled: number }>('/products/backfill-opening-stock', {}),
  adjustStock: (id: string, dto: { date: string; qtyChange: number; reason: string; notes?: string }) =>
    api.post<StockAdjustment>(`/products/${id}/adjust`, dto),
  getFifoCost: (id: string) => api.get<{ fifoCost: number; remainingQty: number }>(`/products/${id}/fifo-cost`),
  // The shared FIFO valuation engine Balance Sheet's Stock Value, the P&L's
  // Opening/Closing Stock, and the Stock Movement Report's Closing Value
  // all now call — one server-side source of truth. `asOf` omitted = today.
  stockValuation: (asOf?: string) =>
    api.get<{ totalValue: number; byProduct: { productId: string; qty: number; avgRate: number; value: number }[] }>(
      `/products/stock-valuation${asOf ? `?asOf=${asOf}` : ''}`,
    ),
  getAdjustments: (id: string, from?: string, to?: string) => {
    const p = new URLSearchParams();
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    return api.get<StockAdjustment[]>(`/products/${id}/adjustments?${p.toString()}`);
  },
  getAllAdjustments: (from?: string, to?: string) => {
    const p = new URLSearchParams();
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    return api.get<(StockAdjustment & { product: { name: string; unit: string } })[]>(`/products/adjustments/all?${p.toString()}`);
  },
};
